import React, { useState } from 'react';
import * as Sentry from '@sentry/react'
import { WizardStepProps } from '@components/Wizard/Wizard';
import PINInput from '@components/PINInput/PINInput';
import LoadingModal from '@components/LoadingModal/LoadingModal';
import { useTranslation } from 'react-i18next';
import BiometricsInitiationBottomSheet from '@components/BiometricsInitiationBottomSheet/BiometricsInitiationBottomSheet';
import { useBiometricsStore } from '@stores/biometricsStore';
import { TonWalletUtils } from '@lib/secrets';
import { Biometrics } from '@lib/telegram';
import { useAuth } from '../../../contexts/AuthContext';
import ReactDOM from 'react-dom';
import { ModalWithIcon } from '@components/ModalWithIcon/ModalWithIcon';
import { delay, redirectToTelegramSupport } from '@lib/utils';
import SlideCaptchaModal from '@components/SlideCaptcha/SlideCaptchaModal';
import { CaptchaRequiredResponse } from '../../../types/captcha';
import { useNavigate } from 'react-router';
import { ApiError } from '@lib/api';

interface PINInputStepProps extends WizardStepProps {
  setWalletMnemonic: (mnemonic: string) => void;
}

const PINInputStep: React.FC<PINInputStepProps> = ({ onNext, setWalletMnemonic }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState(false);
  const [isBiometricsPromptVisible, setIsBiometricsPromptVisible] = React.useState(false);
  const { login, logout } = useAuth();
  const { setBiometrics } = useBiometricsStore();
  const [showModal, setShowModal] = useState(false)
  const [modalTitle, setModalTitle] = useState('')
  const [modalDescription, setModalDescription] = useState('')
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaData, setCaptchaData] = useState<CaptchaRequiredResponse | null>(null);
  const [captchaError, setCaptchaError] = useState<string>('')
  const [currentPin, setCurrentPin] = useState<string>('');
  const [walletData, setWalletData] = useState<{pin: string, mnemonic: string, secretKey: Uint8Array} | null>(null);

  const handlePinComplete = async (result: { success: boolean; newPin?: string }) => {
    try {
      if (result.success && result.newPin) {
        setIsLoading(true);
        setCurrentPin(result.newPin);
        
        // Generate wallet data but don't store it yet
        const tempWalletData = await TonWalletUtils.generateWalletData(result.newPin, 12);
        setWalletData(tempWalletData);

        await delay(1000);

        const registerResult = await TonWalletUtils.registerWalletWithData(tempWalletData);

        if (registerResult && 'captcha_required' in registerResult) {
          // Captcha is required - don't store wallet data yet
          setCaptchaData(registerResult);
          setShowCaptcha(true);
          setIsLoading(false);
        } else {
          // Registration successful, now store wallet data and proceed
          try {
            await TonWalletUtils.storeWalletData(tempWalletData);
            // Only set mnemonic and proceed if storage succeeded
            setWalletMnemonic(tempWalletData.mnemonic);
            login();
            setIsLoading(false);
            proceedAfterRegistration();
          } catch (storageError) {
            // Storage failed - clean up and show error
            await TonWalletUtils.clear();
            await TonWalletUtils.clearToken();
            logout();
            setIsLoading(false);
            setModalTitle(t('page.createWallet.steps.pin.errorText'));
            setModalDescription(t('page.createWallet.steps.pin.errorTextDescription'));
            setShowModal(true);
          }
        }
      }
    } catch (error) {
      Sentry.captureException(error)
      await logout()
      setIsLoading(false);
      setModalTitle(t('page.createWallet.steps.pin.errorText'))
      setModalDescription(t('page.createWallet.steps.pin.errorTextDescription'))
      setShowModal(true)
    }
  };

  const proceedAfterRegistration = () => {
    if (Biometrics.isBiometricsInited() && Biometrics.isAvailable()) {
      setIsBiometricsPromptVisible(true);
    } else {
      onNext?.();
    }
  };

  const handleCaptchaSuccess = async (captcha_key: string, slide_x: number, slide_y: number): Promise<boolean> => {
    setIsLoading(true);
    setCaptchaError('');

    try {
      if (!walletData) {
        setCaptchaError('Wallet data not found. Please try again.');
        setIsLoading(false);
        return false;
      }
      
      const registerResult = await TonWalletUtils.registerWalletWithData(walletData, captcha_key, slide_x, slide_y);

      if (registerResult && 'captcha_required' in registerResult) {
        // Captcha failed, show new captcha
        setCaptchaData(registerResult);
        setIsLoading(false);
        return false;
      } else {
        // Registration successful, now store wallet data
        if (walletData) {
          try {
            await TonWalletUtils.storeWalletData(walletData);
            // Only set mnemonic and proceed if storage succeeded
            setWalletMnemonic(walletData.mnemonic);
            login();
            setShowCaptcha(false);
            proceedAfterRegistration();
            setIsLoading(false);
            return true;
          } catch (storageError) {
            // Storage failed - clean up and show error
            await TonWalletUtils.clear();
            await TonWalletUtils.clearToken();
            setCaptchaError(t('page.createWallet.steps.pin.errorTextDescription'));
            setIsLoading(false);
            return false;
          }
        }
        setCaptchaError('Wallet data not found. Please try again.');
        setIsLoading(false);
        return false;
      }
    } catch (error) {
      // Extract error message
      let errorMsg = t('errors.genericServerDescription');
      if (error instanceof ApiError) {
        const translationKey = `errors.${error.errorCode}.description`;
        const translatedMessage = t(translationKey);
        errorMsg = translatedMessage !== translationKey
          ? translatedMessage
          : t('errors.genericServerDescription');
      }

      // Dismiss captcha modal
      setShowCaptcha(false);
      setCaptchaData(null);
      setCaptchaError('');
      setIsLoading(false);

      // Show error in modal
      setModalTitle(t('page.createWallet.steps.pin.errorText'));
      setModalDescription(errorMsg);
      setShowModal(true);

      return false;
    }
  };

  const handleCaptchaClose = () => {
    setShowCaptcha(false);
    setCaptchaData(null);
    setCaptchaError('');
    // Reset to allow user to try registration again
    setIsLoading(false);
  };

  const handleCaptchaOverlayClose = () => {
    handleCaptchaClose();
    // Navigate back to previous page
    navigate(-1);
  };

  const handleBiometricsEnable = () => {
    setBiometrics(true);
    handleNext();
  }

  const handleNext = () => {
    setIsBiometricsPromptVisible(false);
    setTimeout(() => {
      onNext?.();
    }, 500);
  }

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <PINInput
        mode="create"
        onComplete={handlePinComplete}
      />
      {ReactDOM.createPortal(
        <ModalWithIcon
          title={modalTitle}
          description={modalDescription}
          onPositive={() => {
            redirectToTelegramSupport('JagadCS_bot');
            setShowModal(false);
          }}
          positiveText={t('page.createWallet.steps.pin.errorButtonText')}
          type="failed"
          visible={showModal}
          onModalOverlayClick={() => setShowModal(false)}
        />,
        document.body)}
      <LoadingModal visible={isLoading} message={t('page.createWallet.steps.pin.loading')} />
      <BiometricsInitiationBottomSheet
        isOpen={isBiometricsPromptVisible}
        onSkip={handleNext}
        onEnable={handleBiometricsEnable}
      />
      <SlideCaptchaModal
        isOpen={showCaptcha}
        onClose={handleCaptchaClose}
        onOverlayClose={handleCaptchaOverlayClose}
        onSuccess={handleCaptchaSuccess}
        captchaData={captchaData}
        error={captchaError}
      />
    </div>
  );
};

export default PINInputStep;
